import { makeAutoObservable } from 'mobx';
import { MANAGER_UPGRADES, ManagerUpgradeType } from './data/managerData';
import { addMilliseconds, addSeconds } from 'date-fns';
import { DATA } from './data';
import { TripData } from './data.type';
import { AirportSlug } from './data/airportData';

export class Manager {
  public upgrades: { [k in ManagerUpgradeType]: number } = { airplanes_per_second: 0, ticket_price_multiplier: 0 };

  public nextAirplaneAt: Date | null = null;

  private _nextAirplaneTimeout: number | null = null;

  constructor(
    public airportSlug: AirportSlug,
    public priceStep: number,
  ) {
    makeAutoObservable(this);
    this.checkAirplane();
  }

  getUpgradeValue(type: ManagerUpgradeType) {
    return MANAGER_UPGRADES[type].valueFn(this.upgrades[type]);
  }

  getUpgradePrice(type: ManagerUpgradeType) {
    return Math.round(MANAGER_UPGRADES[type].priceFn(this.upgrades[type], this.priceStep));
  }

  increaseUpgradeLevel(type: ManagerUpgradeType) {
    this.upgrades[type]++;
    this.upgrades[type] = Math.min(this.upgrades[type], MANAGER_UPGRADES[type].maxValue);
  }

  checkAirplane() {
    const trips = this.getAvailableTrips();
    const tripsDescendingProfit = trips.sort((a, b) => b.profit - a.profit);
    const trip = tripsDescendingProfit[0];

    let delayMs = 1000 * (1 / this.getUpgradeValue('airplanes_per_second'));
    if (trip) {
      trip.airplane.beginTrip(trip);
    }

    this.nextAirplaneAt = addMilliseconds(new Date(), delayMs);
    this._nextAirplaneTimeout = window.setTimeout(() => {
      this.checkAirplane();
    }, delayMs);
  }

  getAvailableTrips() {
    if (this.airportSlug === DATA.planTripPopup) {
      return [];
    }
    const airplanes = DATA.airplanes.filter((a) => a.status === 'idle' && a.airportSlug === this.airportSlug);
    const trips: TripData[] = [];

    airplanes.forEach((airplane) => {
      const airport = DATA.airportBySlug(airplane.airportSlug);
      const otherAirports = DATA.airports.filter((a) => a.slug !== airport.slug && a.isAvailable);
      otherAirports.forEach((otherAirport) => {
        trips.push(airplane.computeTripData(airport, otherAirport));
      });
    });

    return trips;
  }

  restart() {
    if (this._nextAirplaneTimeout) {
      clearTimeout(this._nextAirplaneTimeout);
    }
    this._nextAirplaneTimeout = null;
    this.nextAirplaneAt = null;
    this.checkAirplane();
  }

  dispose() {
    if (this._nextAirplaneTimeout) {
      clearTimeout(this._nextAirplaneTimeout);
    }
  }
}
